import type { ReactNode } from 'react'

/** A page's root inside AppShell's `<main>`, which no longer scrolls itself
 * (MS-996): a column that fills the main area, so the page header and product
 * tabs stay put and only the PageScrollArea below them scrolls. */
export function PageView({ children, className = '' }: Readonly<{ children: ReactNode; className?: string }>) {
  return <div className={`flex-1 min-h-0 flex flex-col ${className}`}>{children}</div>
}

/** The prototype's `.tab-scroll-area` (subscription-portal.html): the page's
 * content below its fixed header and tabs, scrolling on its own. The negative
 * right margin takes it to the window's right edge (AppShell's `<main>` pads
 * 32px, `px-8`), so the scrollbar sits there, and the matching right padding
 * keeps the cards aligned with the header and tabs above. The gutter is
 * always reserved, so a card's width never changes when the scrollbar comes
 * and goes; the bottom gap mirrors the tabs-to-card gap above. Because the
 * gutter is always there, each direct child pulls back into it by `--sbw`
 * (the measured scrollbar width, lib/scrollbarWidth.ts), so on a classic
 * scrollbar the cards still end where the tab separator does; on an overlay
 * scrollbar `--sbw` is 0 and this does nothing. */
export function PageScrollArea({
  children,
  className = '',
  stableGutter = true,
}: Readonly<{
  children: ReactNode
  className?: string
  /** Reserve the scrollbar gutter and pull the children back into it (the
   * `.tab-scroll-area`). Home's `.home-columns` is a plain `overflow-y: auto`
   * with neither, so its cards take the full width while nothing scrolls. */
  stableGutter?: boolean
}>) {
  const gutter = stableGutter ? '[scrollbar-gutter:stable] [&>*]:mr-[calc(-1*var(--sbw,0px))]' : ''
  return (
    <div
      data-testid="page-scroll-area"
      className={`flex-1 min-h-0 overflow-y-auto -mr-8 pr-8 pb-6 ${gutter} ${className}`}
    >
      {children}
    </div>
  )
}
